class TBCartDrawerProgress extends HTMLElement {
  connectedCallback() {
    this.applyExternalConfig();

    this.refreshElements();

    this.durationMs = Math.max(3, Number(this.dataset.reservationMinutes || 8)) * 60 * 1000;
    this.total = Number(this.dataset.cartTotalCents || 0);
    this.itemCount = Number(this.dataset.cartItemCount || 0);

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handlePossibleCartChange = this.handlePossibleCartChange.bind(this);

    document.addEventListener('cart:update', this.handleCartUpdate);
    document.addEventListener('quantity-selector:update', this.handlePossibleCartChange);
    document.addEventListener('click', this.handlePossibleCartChange);

    this.syncReservation(false);
    this.render();
    this.fetchCart(true);
  }

  refreshElements() {
    this.message = this.querySelector('[data-tb-progress-message]');
    this.fill = this.querySelector('[data-tb-progress-fill]');
    this.timer = this.querySelector('[data-tb-reservation-time]');
    this.milestones = [...this.querySelectorAll('[data-tb-cart-milestone]')]
      .map((element) => ({
        element,
        label: element.dataset.label || '',
        threshold: Number(element.dataset.thresholdCents || 0),
        unlockedMessage: element.dataset.unlockedMessage || '',
      }))
      .filter((milestone) => milestone.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);
    this.updateMilestonePositions();
  }

  updateMilestonePositions() {
    const finalThreshold = this.milestones[this.milestones.length - 1]?.threshold || 0;
    if (!finalThreshold) return;

    this.milestones.forEach((milestone, index) => {
      const position = Math.min(100, Math.max(0, (milestone.threshold / finalThreshold) * 100));
      milestone.element.style.setProperty('--tb-milestone-position', `${position}%`);
      milestone.element.classList.toggle('is-first', index === 0);
      milestone.element.classList.toggle('is-last', index === this.milestones.length - 1);
    });
  }

  applyExternalConfig() {
    const configScript =
      document.getElementById('tb-cart-drawer-config') ||
      document.getElementById('tb-cart-drawer-config-json');
    if (!configScript?.textContent) return;

    let config;
    try {
      config = JSON.parse(configScript.textContent);
    } catch {
      return;
    }

    const settings = config.settings || {};
    if (typeof settings.enable_shimmer === 'boolean') {
      this.classList.toggle('tb-cart-progress--shimmer', settings.enable_shimmer);
    }
    if (typeof settings.enable_reservation_timer === 'boolean') {
      this.dataset.reservationEnabled = String(settings.enable_reservation_timer);
      this.querySelector('.tb-cart-progress__reservation')?.toggleAttribute('hidden', !settings.enable_reservation_timer);
    }
    if (settings.reservation_duration_minutes) {
      this.dataset.reservationMinutes = String(settings.reservation_duration_minutes);
    }

    const limit = Math.min(5, Math.max(1, Number(settings.number_of_milestones || 3)));
    const milestones = Array.isArray(config.milestones) ? config.milestones.slice(0, limit) : [];
    const milestoneContainer = this.querySelector('.tb-cart-progress__milestones');
    if (!milestoneContainer || !milestones.length) return;

    milestoneContainer.replaceChildren(
      ...milestones
        .filter((milestone) => Number(milestone.threshold_cents || 0) > 0)
        .map((milestone) => this.createMilestoneElement(milestone))
    );
  }

  createMilestoneElement(milestone) {
    const element = document.createElement('div');
    element.className = 'tb-cart-progress__milestone';
    element.dataset.tbCartMilestone = '';
    element.dataset.thresholdCents = String(milestone.threshold_cents || 0);
    element.dataset.label = milestone.label || '';
    element.dataset.unlockedMessage = milestone.unlocked_message || '';

    const icon = document.createElement('span');
    icon.className = 'tb-cart-progress__milestone-icon';

    if (milestone.icon_url) {
      const image = document.createElement('img');
      image.src = milestone.icon_url;
      image.alt = '';
      image.loading = 'lazy';
      icon.append(image);
    } else if (milestone.icon_svg) {
      icon.innerHTML = milestone.icon_svg;
    } else {
      icon.innerHTML = this.fallbackIcon(milestone.label || '');
    }

    const label = document.createElement('span');
    label.className = 'tb-cart-progress__milestone-label';
    label.textContent = milestone.label || '';

    element.append(icon, label);
    return element;
  }

  fallbackIcon(label) {
    if (/shipping/i.test(label)) {
      return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.8"/><circle cx="18" cy="18" r="1.8"/></svg>';
    }
    if (/chart|reward|gift|mystery|toy/i.test(label)) {
      return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h16v12H4z"/><path d="M12 8v12"/><path d="M4 12h16"/><path d="M7 8c-1.4-1.7-.1-4 1.8-3.2 1.1.5 1.9 1.9 3.2 3.2 1.3-1.3 2.1-2.7 3.2-3.2C17.1 4 18.4 6.3 17 8"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h10a2 2 0 0 1 2 2v14H8a2 2 0 0 1-2-2z"/><path d="M8 4v14a2 2 0 0 0 2 2"/><path d="M10 8h5"/><path d="M10 12h5"/></svg>';
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.handleCartUpdate);
    document.removeEventListener('quantity-selector:update', this.handlePossibleCartChange);
    document.removeEventListener('click', this.handlePossibleCartChange);
    window.clearInterval(this.timerInterval);
    window.clearTimeout(this.pendingFetch);
  }

  handleCartUpdate(event) {
    const cart = event.detail?.resource || event.detail?.data;
    if (cart && typeof cart.total_price === 'number') {
      this.updateFromCart(cart, true);
    }
    this.fetchCart(true, 450);
  }

  handlePossibleCartChange(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (
      target.closest('.cart-items__remove') ||
      target.closest('cart-quantity-selector-component') ||
      target.closest('[data-testid="cart-drawer-trigger"]') ||
      target.closest('[name="add"]') ||
      target.closest('form[action*="/cart/add"]')
    ) {
      this.fetchCart(true, 700);
    }
  }

  fetchCart(resetWhenCountChanges, delay = 0) {
    window.clearTimeout(this.pendingFetch);
    this.pendingFetch = window.setTimeout(async () => {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        this.updateFromCart(cart, resetWhenCountChanges);
      } catch (error) {
        // Cart progress is secondary UI; native cart behavior should stay untouched.
      }
    }, delay);
  }

  updateFromCart(cart, resetWhenCountChanges) {
    this.applyExternalConfig();
    this.refreshElements();

    const nextCount = Number(cart.item_count || 0);
    const previousStoredCount = Number(sessionStorage.getItem('tbCartReserveItemCount') || this.itemCount || 0);
    this.total = Number(cart.total_price || 0);
    this.itemCount = nextCount;
    this.dataset.cartTotalCents = String(this.total);
    this.dataset.cartItemCount = String(this.itemCount);

    if (resetWhenCountChanges && nextCount !== previousStoredCount) {
      this.resetReservation(nextCount);
    } else {
      sessionStorage.setItem('tbCartReserveItemCount', String(nextCount));
    }

    this.render();
  }

  resetReservation(itemCount = this.itemCount) {
    if (this.dataset.reservationEnabled !== 'true') return;
    sessionStorage.setItem('tbCartReserveEndsAt', String(Date.now() + this.durationMs));
    sessionStorage.setItem('tbCartReserveItemCount', String(itemCount));
    this.syncReservation(false);
  }

  syncReservation(forceReset) {
    if (this.dataset.reservationEnabled !== 'true' || !this.timer) return;

    const currentEnd = Number(sessionStorage.getItem('tbCartReserveEndsAt') || 0);
    if (forceReset || !currentEnd || currentEnd <= Date.now()) {
      this.resetReservation();
      return;
    }

    sessionStorage.setItem('tbCartReserveItemCount', String(this.itemCount));
    window.clearInterval(this.timerInterval);
    const tick = () => {
      const remaining = Math.max(0, currentEnd - Date.now());
      const totalSeconds = Math.ceil(remaining / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      this.timer.textContent = `${minutes}:${seconds}`;

      if (remaining <= 0) {
        window.clearInterval(this.timerInterval);
      }
    };
    tick();
    this.timerInterval = window.setInterval(tick, 1000);
  }

  render() {
    if (!this.milestones.length || !this.fill || !this.message) return;

    const nextMilestone = this.milestones.find((milestone) => this.total < milestone.threshold);
    const finalMilestone = this.milestones[this.milestones.length - 1];
    const finalThreshold = finalMilestone.threshold;
    const percent = finalThreshold > 0 ? Math.min(100, Math.max(0, (this.total / finalThreshold) * 100)) : 0;

    this.fill.style.width = `${percent}%`;

    for (const milestone of this.milestones) {
      milestone.element.classList.toggle('is-filled', this.total >= milestone.threshold);
    }

    if (nextMilestone) {
      const remaining = Math.max(0, nextMilestone.threshold - this.total);
      this.message.textContent = `You're ${this.formatMoney(remaining)} away from ${nextMilestone.label}!`;
      return;
    }

    this.message.textContent =
      finalMilestone.unlockedMessage ||
      `Congrats! You've unlocked ${this.milestones.map((milestone) => milestone.label).join(', ')}!`;
  }

  formatMoney(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }
}

if (!customElements.get('tb-cart-drawer-progress')) {
  customElements.define('tb-cart-drawer-progress', TBCartDrawerProgress);
}